/**
 * useSyncConnections Hooks
 *
 * React Query hooks for managing external calendar sync connections
 * (Google, Microsoft, Apple).
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { api, SyncConnection, RemoteCalendar } from '@/lib/api';

/**
 * BroadcastChannel name used by the calendar OAuth callback page to notify
 * the opener that a new connection landed. Mirrors the contact-book hook so
 * both arches' callback pages follow the same pattern.
 */
export const SYNC_OAUTH_CHANNEL = 'fias-calendar-sync-oauth';

/** Fetch all sync connections for the calendar arche. */
export function useSyncConnections() {
  const queryClient = useQueryClient();

  // Listen for OAuth-callback completions broadcast from the popup window
  // and refresh the connection list so a brand-new connection shows up
  // without needing a manual refresh. Mirror of the contact-book hook.
  useEffect(() => {
    const channel = new BroadcastChannel(SYNC_OAUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.type === 'oauth-complete') {
        queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.connections() });
      }
    };
    return () => channel.close();
  }, [queryClient]);

  return useQuery<SyncConnection[]>({
    queryKey: queryKeys.calendarSync.connections(),
    queryFn: async () => {
      const res = await api.listCalendarSyncConnections();
      return res.connections;
    },
  });
}

/** Initiate OAuth flow for Google or Microsoft. Opens the authorization URL. */
export function useInitSyncOAuth() {
  return useMutation({
    mutationFn: async (provider: 'google' | 'microsoft') => {
      const res = await api.initCalendarSyncOAuth(provider);
      return res;
    },
    onSuccess: (data) => {
      window.open(data.authorizationUrl, '_blank', 'noopener,noreferrer');
    },
  });
}

/** Connect an Apple account using an app-specific password. */
export function useConnectApple() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ username, appPassword }: { username: string; appPassword: string }) => {
      const res = await api.connectCalendarApple(username, appPassword);
      return res.connection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.connections() });
    },
  });
}

/** Disconnect (delete) a sync connection — keep synced events (default) or
 * remove the remote-imported ones (§10 provenance-aware removal). */
export function useDisconnectSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      mode = 'keep',
    }: {
      connectionId: string;
      mode?: 'keep' | 'remove';
    }) => {
      await api.disconnectCalendarSync(connectionId, mode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.connections() });
    },
  });
}

/** Fetch the list of remote calendars for a given connection. */
export function useRemoteCalendars(connectionId: string) {
  return useQuery<RemoteCalendar[]>({
    queryKey: queryKeys.calendarSync.remoteCalendars(connectionId),
    queryFn: async () => {
      const res = await api.listRemoteCalendars(connectionId);
      return res.calendars;
    },
    enabled: !!connectionId,
  });
}
